import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BankingConnectorView } from "@/components/BankingConnectorView";
import { useOrgCurrency } from "@/lib/currency-context";
import {
  Receipt, FileText, Package, Building2, RefreshCw, Calendar,
  Plus, Trash2, Edit, Search, Download, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Clock, Folder, CreditCard, BarChart3
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function ExpenseDocumentTab() {
  const { currency, formatMoney } = useOrgCurrency();
  const [subTab, setSubTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [belege, setBelege] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchQ, setSearchQ] = useState("");

  const headers = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  }, []);

  // --- Data fetching ---
  const fetchDashboard = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/dashboard`, { headers: headers() });
      setDashboard(r.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchInvoices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/invoices`, { headers: headers(), params: { q: searchQ } });
      setInvoices(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers, searchQ]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/suppliers`, { headers: headers(), params: { q: searchQ } });
      setSuppliers(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers, searchQ]);

  const fetchRecurring = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/recurring`, { headers: headers() });
      setRecurring(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchContracts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/contracts`, { headers: headers() });
      setContracts(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchDocuments = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/documents`, { headers: headers(), params: { q: searchQ } });
      setDocuments(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers, searchQ]);

  const fetchBelege = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/expense-studio/belegbox`, { headers: headers() });
      setBelege(r.data || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (subTab === "invoices") fetchInvoices();
    if (subTab === "suppliers") fetchSuppliers();
    if (subTab === "recurring") fetchRecurring();
    if (subTab === "contracts") fetchContracts();
    if (subTab === "documents") fetchDocuments();
    if (subTab === "belegbox") fetchBelege();
  }, [subTab, fetchInvoices, fetchSuppliers, fetchRecurring, fetchContracts, fetchDocuments, fetchBelege]);

  // --- Dashboard View ---
  const DashboardView = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Expense & Document Studio</h2>
      {dashboard ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-gray-500">Gesamtausgaben</div>
              <div className="text-2xl font-bold">{formatMoney(dashboard.gesamt_ausgaben)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500">Bezahlt</div>
              <div className="text-2xl font-bold text-green-600">{formatMoney(dashboard.bezahlt)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500">Offen</div>
              <div className="text-2xl font-bold text-orange-500">{formatMoney(dashboard.offen)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-red-500">Überfällig</div>
              <div className="text-2xl font-bold text-red-600">{formatMoney(dashboard.ueberfaellig)}</div>
            </Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Monatliche Fixkosten</h3>
              <div className="text-2xl font-bold">{formatMoney(dashboard.monatliche_fixkosten)}</div>
              <div className="text-sm text-gray-500 mt-1">Jährlich: {formatMoney(dashboard.jaehrliche_fixkosten)}</div>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Nächste fällige Zahlungen</h3>
              {(dashboard.naechste_faellig || []).length === 0 ? (
                <p className="text-sm text-gray-400">Keine offenen Zahlungen</p>
              ) : (
                <div className="space-y-1">
                  {dashboard.naechste_faellig.map((z, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{z.lieferant || "Unbekannt"}</span>
                      <span className="font-medium">{formatMoney(z.betrag, z.currency)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
          {Object.keys(dashboard.nach_kategorie || {}).length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Ausgaben nach Kategorie</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(dashboard.nach_kategorie).sort((a,b) => b[1]-a[1]).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                    <span className="capitalize">{cat.replace(/_/g, " ")}</span>
                    <span className="font-medium">{formatMoney(amt)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      ) : (
        <p className="text-gray-400">Lade Dashboard...</p>
      )}
    </div>
  );

  // --- Invoices View ---
  const InvoicesView = () => {
    const [form, setForm] = useState({ lieferant_name: "", rechnungsnummer: "", rechnungsdatum: "", betrag_netto: "", steuersatz: "19", betrag_brutto: "", waehrung: "EUR", kategorie: "sonstiges", faelligkeitsdatum: "", status: "neu", notizen: "" });

    const save = async () => {
      try {
        const data = { ...form, betrag_netto: parseFloat(form.betrag_netto) || 0, steuersatz: parseFloat(form.steuersatz) || 19, betrag_brutto: parseFloat(form.betrag_brutto) || 0 };
        if (editItem) {
          await axios.put(`${API}/api/expense-studio/invoices/${editItem.id}`, data, { headers: headers() });
          toast.success("Rechnung aktualisiert");
        } else {
          await axios.post(`${API}/api/expense-studio/invoices`, data, { headers: headers() });
          toast.success("Rechnung angelegt");
        }
        setShowForm(false); setEditItem(null); fetchInvoices();
      } catch (e) { toast.error("Fehler beim Speichern"); }
    };

    const del = async (id) => {
      if (!window.confirm("Rechnung löschen?")) return;
      await axios.delete(`${API}/api/expense-studio/invoices/${id}`, { headers: headers() });
      toast.success("Gelöscht"); fetchInvoices();
    };

    const statusColor = (s) => ({ bezahlt: "text-green-600 bg-green-50", offen: "text-orange-600 bg-orange-50", ueberfaellig: "text-red-600 bg-red-50", neu: "text-blue-600 bg-blue-50" }[s] || "text-gray-600 bg-gray-50");
    const prepareOcr = async (id) => { try { const { data } = await axios.post(`${API}/api/ocr-preparation/incoming-invoices/${id}/prepare`, {}, { headers: headers() }); toast.success(data.notice); fetchInvoices(); } catch (e) { toast.error(e.response?.data?.detail || "OCR-Vorbereitung nicht möglich"); } };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Eingangsrechnungen</h2>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ lieferant_name: "", rechnungsnummer: "", rechnungsdatum: "", betrag_netto: "", steuersatz: "19", betrag_brutto: "", waehrung: "EUR", kategorie: "sonstiges", faelligkeitsdatum: "", status: "neu", notizen: "" }); }} className="gap-1"><Plus className="w-4 h-4" /> Neue Rechnung</Button>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Suchen..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="max-w-xs" />
        </div>
        {showForm && (
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">{editItem ? "Rechnung bearbeiten" : "Neue Eingangsrechnung"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Lieferant</Label><Input value={form.lieferant_name} onChange={(e) => setForm({...form, lieferant_name: e.target.value})} /></div>
              <div><Label>Rechnungsnr.</Label><Input value={form.rechnungsnummer} onChange={(e) => setForm({...form, rechnungsnummer: e.target.value})} /></div>
              <div><Label>Rechnungsdatum</Label><Input type="date" value={form.rechnungsdatum} onChange={(e) => setForm({...form, rechnungsdatum: e.target.value})} /></div>
              <div><Label>Betrag netto</Label><Input type="number" step="0.01" value={form.betrag_netto} onChange={(e) => setForm({...form, betrag_netto: e.target.value})} /></div>
              <div><Label>MwSt %</Label><Input type="number" value={form.steuersatz} onChange={(e) => setForm({...form, steuersatz: e.target.value})} /></div>
              <div><Label>Betrag brutto</Label><Input type="number" step="0.01" value={form.betrag_brutto} onChange={(e) => setForm({...form, betrag_brutto: e.target.value})} /></div>
              <div><Label>Fälligkeitsdatum</Label><Input type="date" value={form.faelligkeitsdatum} onChange={(e) => setForm({...form, faelligkeitsdatum: e.target.value})} /></div>
              <div><Label>Kategorie</Label>
                <Select value={form.kategorie} onValueChange={(v) => setForm({...form, kategorie: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="software">Software & Abos</SelectItem>
                    <SelectItem value="hosting">Hosting & Cloud</SelectItem>
                    <SelectItem value="telefon">Telefon</SelectItem>
                    <SelectItem value="werbung">Werbung</SelectItem>
                    <SelectItem value="versicherung">Versicherungen</SelectItem>
                    <SelectItem value="miete">Miete</SelectItem>
                    <SelectItem value="freelancer">Freelancer</SelectItem>
                    <SelectItem value="lieferanten">Lieferanten</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neu">Neu</SelectItem>
                    <SelectItem value="zu_pruefen">Zu prüfen</SelectItem>
                    <SelectItem value="freigegeben">Freigegeben</SelectItem>
                    <SelectItem value="offen">Offen</SelectItem>
                    <SelectItem value="bezahlt">Bezahlt</SelectItem>
                    <SelectItem value="ueberfaellig">Überfällig</SelectItem>
                    <SelectItem value="storniert">Storniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notizen</Label><Textarea value={form.notizen} onChange={(e) => setForm({...form, notizen: e.target.value})} rows={2} /></div>
            <div className="flex gap-2">
              <Button onClick={save}>Speichern</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {invoices.length === 0 ? <p className="text-gray-400 text-sm">Keine Eingangsrechnungen vorhanden</p> : invoices.map((inv) => (
            <Card key={inv.id} className="p-3 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium">{inv.lieferant_name || "Unbekannt"}</div>
                <div className="text-sm text-gray-500">{inv.rechnungsnummer} | {inv.rechnungsdatum}</div>
              </div>
              <div className="text-right flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor(inv.status)}`}>{inv.status}</span>
                <span className="font-bold">{formatMoney(inv.betrag_brutto, inv.waehrung || inv.currency || currency)}</span>
                <Button size="sm" variant="ghost" title="OCR vorbereiten (keine externe Übertragung)" onClick={() => prepareOcr(inv.id)}>OCR</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(inv); setForm(inv); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(inv.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Suppliers View ---
  const SuppliersView = () => {
    const [form, setForm] = useState({ firmenname: "", ansprechpartner: "", email: "", telefon: "", strasse: "", plz_ort: "", iban: "", notizen: "" });

    const save = async () => {
      try {
        if (editItem) {
          await axios.put(`${API}/api/expense-studio/suppliers/${editItem.id}`, form, { headers: headers() });
          toast.success("Lieferant aktualisiert");
        } else {
          await axios.post(`${API}/api/expense-studio/suppliers`, form, { headers: headers() });
          toast.success("Lieferant angelegt");
        }
        setShowForm(false); setEditItem(null); fetchSuppliers();
      } catch (e) { toast.error("Fehler"); }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Lieferanten</h2>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ firmenname: "", ansprechpartner: "", email: "", telefon: "", strasse: "", plz_ort: "", iban: "", notizen: "" }); }} className="gap-1"><Plus className="w-4 h-4" /> Neuer Lieferant</Button>
        </div>
        <Input placeholder="Suchen..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="max-w-xs" />
        {showForm && (
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">{editItem ? "Lieferant bearbeiten" : "Neuer Lieferant"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Firmenname</Label><Input value={form.firmenname} onChange={(e) => setForm({...form, firmenname: e.target.value})} /></div>
              <div><Label>Ansprechpartner</Label><Input value={form.ansprechpartner} onChange={(e) => setForm({...form, ansprechpartner: e.target.value})} /></div>
              <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Telefon</Label><Input value={form.telefon} onChange={(e) => setForm({...form, telefon: e.target.value})} /></div>
              <div><Label>Adresse</Label><Input value={form.strasse} onChange={(e) => setForm({...form, strasse: e.target.value})} /></div>
              <div><Label>PLZ / Ort</Label><Input value={form.plz_ort} onChange={(e) => setForm({...form, plz_ort: e.target.value})} /></div>
              <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({...form, iban: e.target.value})} /></div>
            </div>
            <div><Label>Notizen</Label><Textarea value={form.notizen} onChange={(e) => setForm({...form, notizen: e.target.value})} rows={2} /></div>
            <div className="flex gap-2">
              <Button onClick={save}>Speichern</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {suppliers.length === 0 ? <p className="text-gray-400 text-sm">Keine Lieferanten vorhanden</p> : suppliers.map((s) => (
            <Card key={s.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{s.firmenname}</div>
                <div className="text-sm text-gray-500">{s.ansprechpartner} {s.email && `| ${s.email}`}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(s); setForm(s); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/expense-studio/suppliers/${s.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchSuppliers(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Recurring Costs View ---
  const RecurringView = () => {
    const [form, setForm] = useState({ anbieter: "", bezeichnung: "", betrag: "", intervall: "monatlich", kategorie: "software", naechster_termin: "", kuendigungsfrist: "", auto_verlaengerung: false });

    const save = async () => {
      try {
        const data = { ...form, betrag: parseFloat(form.betrag) || 0 };
        if (editItem) {
          await axios.put(`${API}/api/expense-studio/recurring/${editItem.id}`, data, { headers: headers() });
          toast.success("Aktualisiert");
        } else {
          await axios.post(`${API}/api/expense-studio/recurring`, data, { headers: headers() });
          toast.success("Angelegt");
        }
        setShowForm(false); setEditItem(null); fetchRecurring();
      } catch (e) { toast.error("Fehler"); }
    };

    const monthlyTotal = recurring.reduce((sum, r) => {
      if (r.intervall === "monatlich") return sum + (r.betrag || 0);
      if (r.intervall === "jaehrlich") return sum + (r.betrag || 0) / 12;
      if (r.intervall === "quartalsweise") return sum + (r.betrag || 0) / 3;
      return sum;
    }, 0);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Wiederkehrende Kosten & Abos</h2>
            <p className="text-sm text-gray-500">Monatliche Gesamtkosten: {formatMoney(monthlyTotal, currency)}</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ anbieter: "", bezeichnung: "", betrag: "", intervall: "monatlich", kategorie: "software", naechster_termin: "", kuendigungsfrist: "", auto_verlaengerung: false }); }} className="gap-1"><Plus className="w-4 h-4" /> Neues Abo</Button>
        </div>
        {showForm && (
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Anbieter</Label><Input value={form.anbieter} onChange={(e) => setForm({...form, anbieter: e.target.value})} /></div>
              <div><Label>Bezeichnung</Label><Input value={form.bezeichnung} onChange={(e) => setForm({...form, bezeichnung: e.target.value})} /></div>
              <div><Label>Betrag</Label><Input type="number" step="0.01" value={form.betrag} onChange={(e) => setForm({...form, betrag: e.target.value})} /></div>
              <div><Label>Intervall</Label>
                <Select value={form.intervall} onValueChange={(v) => setForm({...form, intervall: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monatlich">Monatlich</SelectItem>
                    <SelectItem value="quartalsweise">Quartalsweise</SelectItem>
                    <SelectItem value="jaehrlich">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nächster Termin</Label><Input type="date" value={form.naechster_termin} onChange={(e) => setForm({...form, naechster_termin: e.target.value})} /></div>
              <div><Label>Kündigungsfrist</Label><Input value={form.kuendigungsfrist} onChange={(e) => setForm({...form, kuendigungsfrist: e.target.value})} placeholder="z.B. 30 Tage" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save}>Speichern</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {recurring.length === 0 ? <p className="text-gray-400 text-sm">Keine wiederkehrenden Kosten</p> : recurring.map((r) => (
            <Card key={r.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{r.anbieter}</div>
                <div className="text-sm text-gray-500">{r.bezeichnung} | {r.intervall}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold">{formatMoney(r.betrag, r.waehrung || r.currency || currency)}</span>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(r); setForm(r); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/expense-studio/recurring/${r.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchRecurring(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Contracts View ---
  const ContractsView = () => {
    const [form, setForm] = useState({ vertragspartner: "", vertragsart: "", bezeichnung: "", beginn: "", ende: "", kosten: "", zahlungsintervall: "monatlich", kuendigungsfrist: "", status: "aktiv" });

    const save = async () => {
      try {
        const data = { ...form, kosten: parseFloat(form.kosten) || 0 };
        if (editItem) {
          await axios.put(`${API}/api/expense-studio/contracts/${editItem.id}`, data, { headers: headers() });
          toast.success("Aktualisiert");
        } else {
          await axios.post(`${API}/api/expense-studio/contracts`, data, { headers: headers() });
          toast.success("Vertrag angelegt");
        }
        setShowForm(false); setEditItem(null); fetchContracts();
      } catch (e) { toast.error("Fehler"); }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Verträge</h2>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ vertragspartner: "", vertragsart: "", bezeichnung: "", beginn: "", ende: "", kosten: "", zahlungsintervall: "monatlich", kuendigungsfrist: "", status: "aktiv" }); }} className="gap-1"><Plus className="w-4 h-4" /> Neuer Vertrag</Button>
        </div>
        {showForm && (
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Vertragspartner</Label><Input value={form.vertragspartner} onChange={(e) => setForm({...form, vertragspartner: e.target.value})} /></div>
              <div><Label>Vertragsart</Label><Input value={form.vertragsart} onChange={(e) => setForm({...form, vertragsart: e.target.value})} placeholder="z.B. Mietvertrag" /></div>
              <div><Label>Bezeichnung</Label><Input value={form.bezeichnung} onChange={(e) => setForm({...form, bezeichnung: e.target.value})} /></div>
              <div><Label>Beginn</Label><Input type="date" value={form.beginn} onChange={(e) => setForm({...form, beginn: e.target.value})} /></div>
              <div><Label>Ende</Label><Input type="date" value={form.ende} onChange={(e) => setForm({...form, ende: e.target.value})} /></div>
              <div><Label>Kosten</Label><Input type="number" step="0.01" value={form.kosten} onChange={(e) => setForm({...form, kosten: e.target.value})} /></div>
              <div><Label>Kündigungsfrist</Label><Input value={form.kuendigungsfrist} onChange={(e) => setForm({...form, kuendigungsfrist: e.target.value})} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktiv">Aktiv</SelectItem>
                    <SelectItem value="endet_bald">Endet bald</SelectItem>
                    <SelectItem value="gekuendigt">Gekündigt</SelectItem>
                    <SelectItem value="beendet">Beendet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save}>Speichern</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {contracts.length === 0 ? <p className="text-gray-400 text-sm">Keine Verträge vorhanden</p> : contracts.map((c) => (
            <Card key={c.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{c.vertragspartner}</div>
                <div className="text-sm text-gray-500">{c.bezeichnung || c.vertragsart} | {c.beginn} - {c.ende || "unbefristet"}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded ${c.status === "aktiv" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-600"}`}>{c.status}</span>
                <span className="font-bold">{formatMoney(c.kosten, c.waehrung || c.currency || currency)}</span>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(c); setForm(c); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/expense-studio/contracts/${c.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchContracts(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Documents View ---
  const DocumentsView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Dokumentencenter</h2>
        <Button onClick={async () => {
          const name = prompt("Dokumentname:");
          if (!name) return;
          await axios.post(`${API}/api/expense-studio/documents`, { name, kategorie: "sonstige" }, { headers: headers() });
          toast.success("Dokument angelegt"); fetchDocuments();
        }} className="gap-1"><Plus className="w-4 h-4" /> Neues Dokument</Button>
      </div>
      <Input placeholder="Suchen..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="max-w-xs" />
      <div className="space-y-2">
        {documents.length === 0 ? <p className="text-gray-400 text-sm">Keine Dokumente vorhanden</p> : documents.map((d) => (
          <Card key={d.id} className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <div>
                <div className="font-medium">{d.name}</div>
                <div className="text-xs text-gray-500">{d.kategorie} | {d.created_at?.slice(0,10)}</div>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/expense-studio/documents/${d.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchDocuments(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Belegbox View ---
  const BelegboxView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Belegbox</h2>
        <Button onClick={async () => {
          const name = prompt("Belegname / Dateiname:");
          if (!name) return;
          await axios.post(`${API}/api/expense-studio/belegbox`, { dateiname: name, status: "unbearbeitet" }, { headers: headers() });
          toast.success("Beleg hinzugefügt"); fetchBelege();
        }} className="gap-1"><Plus className="w-4 h-4" /> Beleg hinzufügen</Button>
      </div>
      <div className="space-y-2">
        {belege.length === 0 ? <p className="text-gray-400 text-sm">Belegbox ist leer</p> : belege.map((b) => (
          <Card key={b.id} className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-gray-400" />
              <div>
                <div className="font-medium">{b.dateiname || "Unbenannt"}</div>
                <div className="text-xs text-gray-500">{b.status} | {b.created_at?.slice(0,10)}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={async () => {
                await axios.put(`${API}/api/expense-studio/belegbox/${b.id}`, { status: "bearbeitet" }, { headers: headers() });
                toast.success("Als bearbeitet markiert"); fetchBelege();
              }}>Erledigt</Button>
              <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/expense-studio/belegbox/${b.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchBelege(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Main Render ---
  const subTabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "invoices", label: "Eingangsrechnungen", icon: Receipt },
    { id: "belegbox", label: "Belegbox", icon: Folder },
    { id: "suppliers", label: "Lieferanten", icon: Building2 },
    { id: "recurring", label: "Abos & Fixkosten", icon: RefreshCw },
    { id: "contracts", label: "Verträge", icon: FileText },
    { id: "documents", label: "Dokumente", icon: Folder },
    { id: "banking", label: "Banking", icon: CreditCard },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-2 border-b">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => { setSubTab(t.id); setShowForm(false); setEditItem(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${subTab === t.id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "dashboard" && <DashboardView />}
      {subTab === "invoices" && <InvoicesView />}
      {subTab === "belegbox" && <BelegboxView />}
      {subTab === "suppliers" && <SuppliersView />}
      {subTab === "recurring" && <RecurringView />}
      {subTab === "contracts" && <ContractsView />}
      {subTab === "documents" && <DocumentsView />}
      {subTab === "banking" && <BankingConnectorView />}
    </div>
  );
}
